import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    classnames,
    CustomMarkdownEditor,
    Input,
    Thumbnail,
    Toast,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Button, Card } from '@ygtechnologies/rubus-ui';
import { ValidatingTextArea } from '@/components/forms/validation';
import React, { useEffect, useMemo, useState } from 'react';
import { getFileThumbnail, MeetingType, updateLetterSetupData } from '@/services/booking';
import { toDateOnly } from '@/utils/date';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { LetterSetupData } from '../../MeetingOverview';
import { UnitsDialog } from '../DialogModals/UnitsDialog';
import { LetterChecklist } from '../MeetingLetterSetup/MeetingLetterSetup';
import theme from './MeetingLetterText.module.scss';

interface MeetingLetterTextProps {
    letterSetup?: LetterSetupData;
    allMeetingsTypes?: MeetingType[];
    onSaved(): void;
}

const MeetingLetterText: React.FC<MeetingLetterTextProps> = ({ letterSetup, allMeetingsTypes, onSaved }) => {
    const [localSetupData, setLocalSetup] = useState<LetterSetupData>();
    const [showUnitsModal, setShowUnitsModal] = useState<boolean>(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (letterSetup && allMeetingsTypes) {
            if (letterSetup.connectedMeetings === null) {
                letterSetup.connectedMeetings = [];
            }

            if (letterSetup.optionalLetterSetupFields === null) {
                letterSetup.optionalLetterSetupFields = [];
            }

            setLocalSetup(letterSetup);
        }
    }, [letterSetup, allMeetingsTypes]);

    const handleChangeValues = (changes: Partial<LetterSetupData>) => {
        setLocalSetup((oldValue) => ({ ...oldValue, ...changes }));
    };
    const handleSaveSetup = async () => {
        if (localSetupData && localSetupData.projectId && localSetupData.id) {
            try {
                setSaving(true);
                await updateLetterSetupData(localSetupData.projectId, localSetupData.id, localSetupData);
                Toast.success(<Trans>Successfully updated setup</Trans>);
            } catch {
                Toast.error(<Trans>Something went wrong with saving</Trans>);
            } finally {
                setSaving(false);
            }
        }
        triggerHandler(onSaved);
    };

    const handleShowUnitsModal = (show: boolean) => {
        setShowUnitsModal(show);
    };

    const contactTextLength = useMemo(() => {
        const length = localSetupData?.contactFreeText ? localSetupData.contactFreeText.length : 0;
        return length;
    }, [localSetupData]);

    const optionalFields = useMemo(() => {
        if (!letterSetup) {
            return [];
        }

        return LetterChecklist.filter((x) => letterSetup.optionalLetterSetupFields?.includes(x.id));
    }, [letterSetup]);

    const connectedMeetings = useMemo(() => {
        if (!letterSetup || !allMeetingsTypes) {
            return [];
        }

        return letterSetup.connectedMeetings!.map((x) => allMeetingsTypes.find((y) => y.id === x)!);
    }, [letterSetup, allMeetingsTypes]);

    return (
        <>
            {localSetupData && (
                <Card>
                    <div className={theme.headerBar}>
                        <Button onClick={() => handleSaveSetup()} variant="primary" loading={saving}>
                            <Trans>Save</Trans>
                        </Button>
                        <Button onClick={() => handleShowUnitsModal(true)} variant="primary" disabled={saving}>
                            <Trans>Add units and send invitations</Trans>
                        </Button>
                        <UnitsDialog
                            projectId={letterSetup!.projectId!}
                            activeMeetingType={localSetupData.meetingType!}
                            show={showUnitsModal}
                            data={localSetupData}
                            onSend={() => handleShowUnitsModal(false)}
                            onClose={() => handleShowUnitsModal(false)}
                            onCancel={() => handleShowUnitsModal(false)}
                        />
                    </div>
                    <div className={theme.letterWrapper}>
                        <div className={theme.leftContent}>
                            <div className={theme.senderContent}>
                                <div className={theme.logos}>
                                    {localSetupData.logoImageToken && (
                                        <Thumbnail
                                            src={getFileThumbnail(localSetupData.logoImageToken)}
                                            className={theme.image}
                                        />
                                    )}
                                    {localSetupData.supportLogoImageToken && (
                                        <Thumbnail
                                            src={getFileThumbnail(localSetupData.supportLogoImageToken)}
                                            className={theme.image}
                                        />
                                    )}
                                </div>
                                <div
                                    className={classnames({
                                        [theme.senderInfo]:
                                            localSetupData.logoImageToken || localSetupData.supportLogoImageToken,
                                    })}>
                                    <CustomMarkdownEditor
                                        markdown={localSetupData.senderFreeText || ''}
                                        previewMode={true}
                                        mdClassName={classnames(theme.strongFix, theme.removePadding)}
                                    />
                                </div>
                            </div>
                            <div className={theme.textContent}>
                                <div className={classnames(theme.headerText)}>
                                    <Trans>Title and text</Trans>
                                </div>
                                <Input
                                    value={localSetupData.headerText || ''}
                                    placeholder={t`Title of the letter`}
                                    onChange={(e) =>
                                        handleChangeValues({
                                            headerText: e.target.value,
                                        })
                                    }
                                    className={classnames(theme.fullInputBorder, 'form-control')}
                                />
                                <CustomMarkdownEditor
                                    markdown={localSetupData.contentText || ''}
                                    onChange={(text) =>
                                        handleChangeValues({
                                            contentText: text,
                                        })
                                    }
                                    mdClassName={classnames(theme.strongFix, theme.letterText)}
                                    wrapperClassName={theme.letterContent}
                                />
                            </div>
                        </div>
                        <div className={theme.rightContent}>
                            {(optionalFields.length > 0 || connectedMeetings.length > 0) && (
                                <Card className={theme.importantInfo} variant='flat'>
                                    {optionalFields.map((item, index) =>
                                        item.id.includes('Date') ? (
                                            <div key={index} className={theme.importantInfoRow}>
                                                <span className={theme.importantInfoHeader}>{item.caption()}</span>
                                                <Input
                                                    className={classnames(theme.inputColor, 'form-control')}
                                                    disabled
                                                    value={toDateOnly(new Date())}
                                                />
                                            </div>
                                        ) : (
                                            <div key={index} className={theme.importantInfoRow}>
                                                <span className={theme.importantInfoHeader}>{item.caption()}</span>
                                                <Input
                                                    className={classnames(theme.inputColor, 'form-control')}
                                                    disabled
                                                    value={item.templateText}
                                                />
                                            </div>
                                        )
                                    )}
                                    {connectedMeetings?.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <div className={theme.importantInfoRow}>
                                                <span className={theme.importantInfoHeader}>
                                                    {item.name} <Trans>date</Trans>
                                                </span>
                                                <Input
                                                    className={classnames(theme.inputColor, 'form-control')}
                                                    disabled
                                                    value={toDateOnly(new Date())}
                                                />
                                            </div>
                                            <div className={theme.importantInfoRow}>
                                                <span className={theme.importantInfoHeader}>
                                                    {item.name} <Trans>time</Trans>
                                                </span>
                                                <Input
                                                    className={classnames(theme.inputColor, 'form-control')}
                                                    disabled
                                                    value={'12:00-13:00'}
                                                />
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </Card>
                            )}
                            <div className={theme.contactPerson}>
                                <div className={classnames(theme.labelRow)}>
                                    <span className={theme.labelBox}>
                                        <Trans>Contact person</Trans>
                                    </span>
                                    <Input
                                        value={localSetupData.contactName || ''}
                                        placeholder={t`Name of contact`}
                                        onChange={(e) =>
                                            handleChangeValues({
                                                contactName: e.target.value,
                                            })
                                        }
                                        className={classnames(theme.inputBorder, 'form-control')}
                                    />
                                </div>
                                <div className={classnames(theme.labelRow)}>
                                    <span className={theme.labelBox}>
                                        <Trans>Email</Trans>
                                    </span>
                                    <Input
                                        value={localSetupData.contactEmail || ''}
                                        placeholder={t`Email of contact`}
                                        onChange={(e) =>
                                            handleChangeValues({
                                                contactEmail: e.target.value,
                                            })
                                        }
                                        className={classnames(theme.inputBorder, 'form-control')}
                                    />
                                </div>
                                <ValidatingTextArea
                                    className={classnames(theme.fullInputBorder, 'form-control')}
                                    placeholder={t`Write a info message`}
                                    rows={4}
                                    maxLength={200}
                                    value={localSetupData.contactFreeText || ''}
                                    onChange={(e) =>
                                        handleChangeValues({
                                            contactFreeText: e.target.value,
                                        })
                                    }
                                />
                                {200 - contactTextLength} <Trans>signs left</Trans>
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </>
    );
};

export default MeetingLetterText;
